import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

export type DdqInvitationListOptions = {
  /** When false, GP/LP list slices are not written (use {@link listInvitees} instead). */
  updateStore?: boolean;
  /** Sidebar / background checks must not toggle the Invitees page loading indicator. */
  suppressLoading?: boolean;
};

import { ApiService } from '@core/api/api.service';
import { PaginationParams } from '@core/api/api-entity.service';
import {
  DdqGpInvitation,
  DdqGpInvitationListFilters,
  DdqInvitationDirection,
  DdqInvitationListResult,
  DdqInvitationListScope,
  DdqInviterOrganizationOption,
  DdqInviterOrganizationType,
  DdqLpInvitation,
  DdqLpInvitationListFilters,
  DdqInvitationMessageResult,
  PaginatedDdqInvitationResponse,
} from '@core/models/ddq-invitation.model';
import { DdqInvitationStore } from './ddq-invitation.store';
import { mapInvitationsToInviteeRows } from './ddq-invitation.mapper';
import { DdqInviteeRow } from '@core/models/ddq-invitation.model';

const GP_ADMIN_URL = 'subscriptions/invitations/ddq/admin/gp/';
const GP_INVITER_ORGS_URL = 'subscriptions/invitations/ddq/admin/gp/inviter-organizations/';
const GP_MINE_URL = 'subscriptions/invitations/ddq/gp/mine/';
const LP_ADMIN_URL = 'subscriptions/invitations/ddq/admin/lp/';
const LP_INVITER_ORGS_URL = 'subscriptions/invitations/ddq/admin/lp/inviter-organizations/';
const LP_MINE_URL = 'subscriptions/invitations/ddq/lp/mine/';

function invitationMessagesUrl(invitationUid: string): string {
  return `subscriptions/invitations/ddq/${invitationUid}/messages/`;
}

function pickString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
    if (typeof v === 'number' || typeof v === 'boolean') {
      return String(v);
    }
  }
  return null;
}

/**
 * Extract a NestedRelatedField uid (API returns `{ uid, full_name }` / `{ uid, fullName }`)
 * or a bare string uid. Used for FK fields including `invitee_user` → chat `contextId`.
 */
function pickUid(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
    if (v && typeof v === 'object') {
      const nested = v as Record<string, unknown>;
      const uid = nested.uid ?? nested.id;
      if (typeof uid === 'string' && uid.trim() !== '') {
        return uid.trim();
      }
    }
  }
  return null;
}

function mapGpInvitationRow(row: Record<string, unknown>): DdqGpInvitation {
  return {
    uid: pickUid(row.uid) ?? '',
    inviterEmittentCompany: pickUid(row.inviterEmittentCompany, row.inviter_emittent_company),
    inviterEmittentCompanyName: pickString(
      row.inviterEmittentCompanyName,
      row.inviter_emittent_company_name,
    ),
    inviteeEmail: pickString(row.inviteeEmail, row.invitee_email) ?? '',
    inviteeInvestorCompany: pickUid(row.inviteeInvestorCompany, row.invitee_investor_company),
    inviteeInvestorCompanyName: pickString(
      row.inviteeInvestorCompanyName,
      row.invitee_investor_company_name,
    ),
    inviteeClient: pickUid(row.inviteeClient, row.invitee_client),
    inviteeOrgType: pickString(row.inviteeOrgType, row.invitee_org_type),
    inviteeUser: pickUid(row.inviteeUser, row.invitee_user),
    inviteFirstName: pickString(row.inviteFirstName, row.invite_first_name),
    inviteLastName: pickString(row.inviteLastName, row.invite_last_name),
    inviteSalutation: pickString(row.inviteSalutation, row.invite_salutation),
    inviteUserType: pickString(row.inviteUserType, row.invite_user_type),
    inviteCompanyName: pickString(row.inviteCompanyName, row.invite_company_name),
    accessLevel: pickString(row.accessLevel, row.access_level) ?? 'read_only',
    status: pickString(row.status) ?? 'pending',
    invitedAt: pickString(row.invitedAt, row.invited_at),
    acceptedAt: pickString(row.acceptedAt, row.accepted_at),
    ddq: pickUid(row.ddq),
    invitationToken: pickString(row.invitationToken, row.invitation_token),
    invitationType: pickString(row.invitationType, row.invitation_type) ?? 'fillout',
    product: pickUid(row.product),
    createdAt: pickString(row.createdAt, row.created_at),
    modifiedAt: pickString(row.modifiedAt, row.modified_at),
  };
}

function mapLpInvitationRow(row: Record<string, unknown>): DdqLpInvitation {
  return {
    uid: pickUid(row.uid) ?? '',
    inviterInvestorCompany: pickUid(row.inviterInvestorCompany, row.inviter_investor_company),
    inviterInvestorCompanyName: pickString(
      row.inviterInvestorCompanyName,
      row.inviter_investor_company_name,
    ),
    inviterClient: pickUid(row.inviterClient, row.inviter_client),
    inviteeEmail: pickString(row.inviteeEmail, row.invitee_email) ?? '',
    inviteeEmittentCompany: pickUid(row.inviteeEmittentCompany, row.invitee_emittent_company),
    inviteeEmittentCompanyName: pickString(
      row.inviteeEmittentCompanyName,
      row.invitee_emittent_company_name,
    ),
    inviteeUser: pickUid(row.inviteeUser, row.invitee_user),
    issuerName: pickString(row.issuerName, row.issuer_name),
    fundName: pickString(row.fundName, row.fund_name),
    inviteFirstName: pickString(row.inviteFirstName, row.invite_first_name),
    inviteLastName: pickString(row.inviteLastName, row.invite_last_name),
    inviteSalutation: pickString(row.inviteSalutation, row.invite_salutation),
    status: pickString(row.status) ?? 'pending',
    invitedAt: pickString(row.invitedAt, row.invited_at),
    acceptedAt: pickString(row.acceptedAt, row.accepted_at),
    declinedAt: pickString(row.declinedAt, row.declined_at),
    ddq: pickUid(row.ddq),
    invitationToken: pickString(row.invitationToken, row.invitation_token),
    invitationType: pickString(row.invitationType, row.invitation_type) ?? 'fillout',
    product: pickUid(row.product),
    existingIssuerCompany: pickUid(row.existingIssuerCompany, row.existing_issuer_company),
    createdAt: pickString(row.createdAt, row.created_at),
    modifiedAt: pickString(row.modifiedAt, row.modified_at),
  };
}

@Injectable({ providedIn: 'root' })
export class DdqInvitationService {
  constructor(
    private readonly apiService: ApiService,
    private readonly store: DdqInvitationStore,
  ) {}

  /**
   * Resolve list URL for direction + scope (see ddq-invitation-list-api.md).
   */
  resolveListUrl(direction: DdqInvitationDirection, scope: DdqInvitationListScope): string {
    if (direction === 'gp') {
      return scope === 'admin' ? GP_ADMIN_URL : GP_MINE_URL;
    }
    return scope === 'admin' ? LP_ADMIN_URL : LP_MINE_URL;
  }

  /**
   * List GP invitations (GP → LP) with pagination and optional filters.
   * Updates {@link DdqInvitationStore} on success.
   */
  listGpInvitations(
    scope: DdqInvitationListScope,
    pagination?: PaginationParams,
    filters?: DdqGpInvitationListFilters,
    options?: DdqInvitationListOptions,
  ): Observable<DdqInvitationListResult<DdqGpInvitation>> {
    return this.fetchList(
      this.resolveListUrl('gp', scope),
      pagination,
      filters,
      mapGpInvitationRow,
      (rows, pag) => {
        if (options?.updateStore !== false) {
          this.store.setGpInvitations(rows, pag);
        }
      },
      options,
    );
  }

  /**
   * List LP invitations (LP → GP) with pagination and optional filters.
   * Updates {@link DdqInvitationStore} on success.
   */
  listLpInvitations(
    scope: DdqInvitationListScope,
    pagination?: PaginationParams,
    filters?: DdqLpInvitationListFilters,
    options?: DdqInvitationListOptions,
  ): Observable<DdqInvitationListResult<DdqLpInvitation>> {
    return this.fetchList(
      this.resolveListUrl('lp', scope),
      pagination,
      filters,
      mapLpInvitationRow,
      (rows, pag) => {
        if (options?.updateStore !== false) {
          this.store.setLpInvitations(rows, pag);
        }
      },
      options,
    );
  }

  private fetchList<T>(
    url: string,
    pagination: PaginationParams | undefined,
    filters: DdqGpInvitationListFilters | DdqLpInvitationListFilters | undefined,
    mapRow: (row: Record<string, unknown>) => T,
    onSuccess: (rows: T[], pagination: PaginationParams) => void,
    options?: DdqInvitationListOptions,
  ): Observable<DdqInvitationListResult<T>> {
    const trackLoading = options?.suppressLoading !== true;
    if (trackLoading) {
      this.store.setLoading(true);
    }
    const params = this.buildListParams(pagination, filters);

    return this.apiService
      .get<PaginatedDdqInvitationResponse<T>>(url, { params })
      .pipe(
        map((response) => this.normalizeListResponse(response, mapRow)),
        tap(({ results, pagination: pag }) => onSuccess(results, pag)),
        catchError((error) => {
          if (trackLoading) {
            this.store.setErrorState('Failed to load DDQ invitations');
          }
          console.error('Error loading DDQ invitations:', error);
          const empty: DdqInvitationListResult<T> = {
            results: [],
            pagination: { page: 1, pageSize: pagination?.pageSize ?? 10, total: 0 },
          };
          return of(empty);
        }),
        finalize(() => {
          if (trackLoading) {
            this.store.setLoading(false);
          }
        }),
      );
  }

  private normalizeListResponse<T>(
    response: PaginatedDdqInvitationResponse<T> | Record<string, unknown>,
    mapRow: (row: Record<string, unknown>) => T,
  ): DdqInvitationListResult<T> {
    const raw = response as Record<string, unknown>;
    const resultsRaw = (raw.results ?? []) as Record<string, unknown>[];
    const pagRaw = (raw.pagination ?? {}) as Record<string, unknown>;

    const pagination: PaginationParams = {
      page: Number(pagRaw.page ?? 1),
      pageSize: Number(pagRaw.pageSize ?? pagRaw.page_size ?? 10),
      total: Number(pagRaw.total ?? 0),
    };

    return {
      results: resultsRaw.map(mapRow),
      pagination,
    };
  }

  /**
   * Build query params for list endpoints (snake_case; backend also accepts camelCase).
   */
  /**
   * Load invitees for the list screen and update unified store rows.
   */
  /**
   * Staff admin: distinct inviter organizations for the organization filter dropdown.
   * @see DDQGPInvitationInviterOrganizationsView / DDQLPInvitationInviterOrganizationsView
   */
  listInviterOrganizations(direction: DdqInvitationDirection): Observable<DdqInviterOrganizationOption[]> {
    const url = direction === 'gp' ? GP_INVITER_ORGS_URL : LP_INVITER_ORGS_URL;
    return this.apiService.get<Record<string, unknown> | DdqInviterOrganizationOption[]>(url).pipe(
      map((response) => this.normalizeInviterOrganizationsResponse(response)),
      catchError((error) => {
        console.error('Error loading DDQ inviter organizations:', error);
        return of([]);
      }),
    );
  }

  listInvitees(
    direction: DdqInvitationDirection,
    scope: DdqInvitationListScope,
    pagination?: PaginationParams,
    filters?: DdqGpInvitationListFilters | DdqLpInvitationListFilters,
  ): Observable<DdqInvitationListResult<DdqInviteeRow>> {
    const mapResult = (
      result: DdqInvitationListResult<DdqGpInvitation | DdqLpInvitation>,
    ): DdqInvitationListResult<DdqInviteeRow> => {
      const rows = mapInvitationsToInviteeRows(direction, result.results);
      this.store.setInviteeRows(rows, result.pagination);
      if (scope === 'mine' && result.pagination.total !== undefined) {
        this.store.setHasInvitees((result.pagination.total ?? 0) > 0);
      }
      return { results: rows, pagination: result.pagination };
    };

    if (direction === 'gp') {
      return this.listGpInvitations(scope, pagination, filters as DdqGpInvitationListFilters, {
        updateStore: false,
      }).pipe(map(mapResult));
    }
    return this.listLpInvitations(scope, pagination, filters as DdqLpInvitationListFilters, {
      updateStore: false,
    }).pipe(map(mapResult));
  }

  /**
   * Send a chat message for a DDQ invitation (get-or-creates invitation chat).
   * POST /api/subscriptions/invitations/ddq/{invitation_uid}/messages/
   * Normalizes snake_case / camelCase so optimistic UI bubbles get valid createdAt + sender.
   */
  sendMessage(invitationUid: string, text: string): Observable<DdqInvitationMessageResult> {
    const trimmed = (text || '').trim();
    if (!invitationUid || !trimmed) {
      return of({ chatUid: '', text: trimmed });
    }

    return this.apiService.post<Record<string, unknown>>(invitationMessagesUrl(invitationUid), { text: trimmed }).pipe(
      map((response) => this.normalizeInvitationMessageResponse(response, trimmed)),
    );
  }

  private normalizeInvitationMessageResponse(
    response: Record<string, unknown>,
    fallbackText: string,
  ): DdqInvitationMessageResult {
    const chatUid = pickUid(response.chatUid, response.chat_uid) ?? '';
    const senderRaw = response.sender;
    let sender: DdqInvitationMessageResult['sender'];

    if (senderRaw && typeof senderRaw === 'object') {
      const s = senderRaw as Record<string, unknown>;
      sender = {
        ...s,
        uid: pickUid(s.uid, s.id) ?? undefined,
        firstName: pickString(s.firstName, s.first_name) ?? '',
        lastName: pickString(s.lastName, s.last_name) ?? '',
      };
    }

    return {
      uid: pickUid(response.uid) ?? undefined,
      text: pickString(response.text) ?? fallbackText,
      chatUid,
      createdAt:
        pickString(response.createdAt, response.created_at) ?? new Date().toISOString(),
      readAt: pickString(response.readAt, response.read_at),
      sender,
    };
  }

  private normalizeInviterOrganizationsResponse(
    response: Record<string, unknown> | DdqInviterOrganizationOption[],
  ): DdqInviterOrganizationOption[] {
    const rows = Array.isArray(response)
      ? response
      : ((response.results ?? []) as Record<string, unknown>[]);

    return rows
      .map((row) => this.mapInviterOrganizationOption(row))
      .filter((row) => !!row.uid && !!row.name);
  }

  private mapInviterOrganizationOption(row: Record<string, unknown>): DdqInviterOrganizationOption {
    const inviterTypeRaw = pickString(row.inviterType, row.inviter_type);
    const inviterType =
      inviterTypeRaw === 'client' || inviterTypeRaw === 'investor_company'
        ? (inviterTypeRaw as DdqInviterOrganizationType)
        : undefined;

    return {
      uid: pickString(row.uid) ?? '',
      name:
        pickString(row.name, row.fullCompanyName, row.full_company_name) ?? '',
      inviterType,
    };
  }

  buildListParams(
    pagination?: PaginationParams,
    filters?: DdqGpInvitationListFilters | DdqLpInvitationListFilters,
  ): HttpParams {
    let params = new HttpParams();

    if (pagination?.page !== undefined) {
      params = params.set('pagination.page', String(pagination.page));
    }
    if (pagination?.pageSize !== undefined) {
      params = params.set('pagination.page_size', String(pagination.pageSize));
    }

    if (!filters) {
      return params;
    }

    const setIfPresent = (key: string, value: string | undefined) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params = params.set(key, String(value).trim());
      }
    };

    setIfPresent('status', filters.status);
    setIfPresent('invitation_type', filters.invitationType);
    setIfPresent('ddq_id', filters.ddqId);
    setIfPresent('product_id', filters.productId);
    setIfPresent('invitee_email', filters.inviteeEmail);
    setIfPresent('q', filters.q);

    const gpFilters = filters as DdqGpInvitationListFilters;
    setIfPresent('access_level', gpFilters.accessLevel);
    setIfPresent('inviter_emittent_company', gpFilters.inviterEmittentCompany);

    const lpFilters = filters as DdqLpInvitationListFilters;
    setIfPresent('inviter_investor_company', lpFilters.inviterInvestorCompany);
    setIfPresent('inviter_client', lpFilters.inviterClient);

    return params;
  }
}
